import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../api";
import { getT, LANGS } from "../i18n/translations";
import { MarkdownEditor } from "../components/ProductEditor";
import EditorialHero from "../components/EditorialHero";
import { isAdminRole } from "../utils/roles";

/* ═══════════════════════════════════════════════════════════
   SVG ICONS — NO EMOJI ANYWHERE
   ═══════════════════════════════════════════════════════════ */
const SvgArrowUp = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 3l7 7h-4.5v7h-5v-7H3l7-7z" /></svg>
);
const SvgBack = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
);
const SvgPlus = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
);
const SvgComment = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.068.157 2.148.279 3.238.365.3.024.564.172.724.416l2.04 3.083a.75.75 0 001.088.022l2.131-2.745c.176-.227.462-.363.76-.384a48.1 48.1 0 003.307-.37C18.627 15.744 19.75 14.37 19.75 12.76V6.74c0-1.6-1.123-2.994-2.707-3.227A48.4 48.4 0 0012 3c-1.74 0-3.44.1-5.043.288C5.373 3.494 4.25 4.868 4.25 6.47v6.29z" /></svg>
);
const SvgEye = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.43 0 .638C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
);
const SvgPin = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
);
const SvgFire = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31 31 0 00-.613 3.58 2.64 2.64 0 01-1.945-1.164c-.208-.32-.413-.707-.573-1.122A6 6 0 002 11a6.002 6.002 0 0011.215 2.98c.353-.77.553-1.6.658-2.404.09-.682.132-1.486.073-2.324-.052-.736-.175-1.558-.324-2.39-.304-1.692-.677-3.39-1.227-4.31z" /></svg>
);
const SvgTrophy = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 1a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 1zM5.05 3.05a.75.75 0 011.06 0l1.062 1.06a.75.75 0 11-1.06 1.06L5.05 4.11a.75.75 0 010-1.06zm9.9 0a.75.75 0 010 1.06l-1.06 1.06a.75.75 0 01-1.061-1.06l1.06-1.06a.75.75 0 011.06 0zM3 8a7 7 0 1114 0A7 7 0 013 8zm8 0a1 1 0 11-2 0 1 1 0 012 0z" /></svg>
);
const SvgGlobe = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-4.247m0 0A8.966 8.966 0 013 12c0-1.257.257-2.454.722-3.543" /></svg>
);
const SvgReply = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
);
const SvgWrench = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.88 5.88a2.121 2.121 0 01-3-3l5.88-5.88m0 0L4.49 8.24c-.33-.33-.86-1.29.02-2.88 1.2-2.18 4.14-2.97 6.03-1.09l8.22 8.22c1.88 1.89 1.09 4.83-1.09 6.03-1.59.88-2.55.35-2.88.02l-3.97-3.97" /></svg>
);
const SvgStarFill = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
);
const SvgStarEmpty = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
);

/* ── Star Rating display ── */
function StarRating({ rating, size = "sm" }) {
  const sz = size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={i <= rating ? "text-amber-400" : "text-gray-200"}>
          {i <= rating ? <SvgStarFill className={sz} /> : <SvgStarEmpty className={sz} />}
        </span>
      ))}
    </span>
  );
}

/* ── Clickable Star Rating ── */
function StarRatingInput({ rating, setRating }) {
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" onClick={() => setRating(i)}
          className={`transition-transform hover:scale-125 ${i <= rating ? "text-amber-400" : "text-gray-300 hover:text-amber-300"}`}>
          <SvgStarFill className="w-6 h-6" />
        </button>
      ))}
    </span>
  );
}

const BADGE_COLORS = {
  1: { bg: "bg-gray-100", text: "text-gray-500", label: "A4" },
  2: { bg: "bg-blue-50", text: "text-blue-600", label: "Inkjet" },
  3: { bg: "bg-purple-50", text: "text-purple-600", label: "Laser" },
  4: { bg: "bg-amber-50", text: "text-amber-600", label: "Master" },
};

function BadgeTag({ level, name }) {
  const s = BADGE_COLORS[level] || BADGE_COLORS[1];
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${s.bg} ${s.text}`}>
      <SvgStarFill className="w-2.5 h-2.5" /> {name || s.label}
    </span>
  );
}

function timeSince(dateStr, lang = "ko") {
  const d = new Date(dateStr);
  const s = Math.floor((Date.now() - d) / 1000);
  if (lang === "ko") {
    if (s < 60) return "방금 전";
    if (s < 3600) return `${Math.floor(s / 60)}분 전`;
    if (s < 86400) return `${Math.floor(s / 3600)}시간 전`;
    if (s < 604800) return `${Math.floor(s / 86400)}일 전`;
  } else if (lang === "ja") {
    if (s < 60) return "たった今";
    if (s < 3600) return `${Math.floor(s / 60)}分前`;
    if (s < 86400) return `${Math.floor(s / 3600)}時間前`;
    if (s < 604800) return `${Math.floor(s / 86400)}日前`;
  } else if (lang === "de") {
    if (s < 60) return "gerade eben";
    if (s < 3600) return `vor ${Math.floor(s / 60)} Min.`;
    if (s < 86400) return `vor ${Math.floor(s / 3600)} Std.`;
    if (s < 604800) return `vor ${Math.floor(s / 86400)} T.`;
  } else if (lang === "ru") {
    if (s < 60) return "только что";
    if (s < 3600) return `${Math.floor(s / 60)} мин. назад`;
    if (s < 86400) return `${Math.floor(s / 3600)} ч. назад`;
    if (s < 604800) return `${Math.floor(s / 86400)} дн. назад`;
  } else {
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  }
  const locale = { ko: "ko-KR", en: "en-US", ja: "ja-JP", ru: "ru-RU", de: "de-DE" };
  return d.toLocaleDateString(locale[lang] || "en-US");
}


/* ═══════════════════════════════════════════════════════════
   LANGUAGE SWITCHER
   ═══════════════════════════════════════════════════════════ */
function LangSwitcher({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-[#6e6e73] hover:bg-gray-100 transition">
        <SvgGlobe className="w-3.5 h-3.5" /> {current.label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden min-w-[120px]">
          {LANGS.map(l => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition ${l.code === lang ? "bg-blue-50 text-[#0071e3] font-medium" : "text-[#1d1d1f]"}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Rich content renderer ── */
function RichContent({ content, format }) {
  if (format === "markdown") {
    return <div className="prose max-w-none text-sm text-[#1d1d1f] leading-relaxed"><ReactMarkdown>{content || ""}</ReactMarkdown></div>;
  }
  if (format === "html") {
    return <div className="prose max-w-none text-sm text-[#1d1d1f] leading-relaxed" dangerouslySetInnerHTML={{ __html: content || "" }} />;
  }
  return <div className="text-sm text-[#1d1d1f] leading-relaxed whitespace-pre-wrap">{content}</div>;
}


/* ═══════════════════════════════════════════════════════════
   REVIEW SUMMARY BOX
   ═══════════════════════════════════════════════════════════ */
function ReviewSummary({ category, lang }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    const params = category ? `?category=${category}` : "";
    api.get(`/api/community/review-summary${params}`).then(r => setSummary(r.data)).catch(() => {});
  }, [category]);

  if (!summary || !summary.review_count) return null;

  const bars = [
    { label: "5", count: summary.r5 || 0 },
    { label: "4", count: summary.r4 || 0 },
    { label: "3", count: summary.r3 || 0 },
    { label: "2", count: summary.r2 || 0 },
    { label: "1", count: summary.r1 || 0 },
  ];
  const max = Math.max(...bars.map(b => b.count), 1);

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-6">
        <div className="text-center shrink-0">
          <div className="text-3xl font-bold text-[#1d1d1f]">{summary.avg_rating || 0}</div>
          <StarRating rating={Math.round(summary.avg_rating || 0)} size="md" />
          <p className="text-[11px] text-[#86868b] mt-1">
            {lang === "ko" ? `${summary.review_count}개 리뷰` : `${summary.review_count} reviews`}
          </p>
        </div>
        <div className="flex-1 space-y-1">
          {bars.map(b => (
            <div key={b.label} className="flex items-center gap-2 text-[11px]">
              <span className="w-3 text-[#86868b] text-right">{b.label}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${(b.count / max) * 100}%` }} />
              </div>
              <span className="w-6 text-[#86868b] text-right">{b.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function CommunityPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const postIdParam = searchParams.get("post");
  const navigate = useNavigate();
  const { user } = useAuth();

  const { lang } = useI18n();
  const t = getT(lang);

  const [categories, setCategories] = useState([]);
  const [activeSlug, setActiveSlug] = useState(slug || "");
  const [sort, setSort] = useState("latest");
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewPost, setViewPost] = useState(null);
  const [showWrite, setShowWrite] = useState(false);
  const [writeAsReview, setWriteAsReview] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [nicknameModal, setNicknameModal] = useState(false);
  const [nickInput, setNickInput] = useState("");
  const [nickError, setNickError] = useState("");
  const [toast, setToast] = useState("");


  // Fetch categories
  useEffect(() => {
    api.get(`/api/community/categories?lang=${lang}`)
      .then(r => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, [lang]);

  // Fetch posts
  const fetchPosts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, sort, per_page: 20, lang });
    if (activeSlug) params.set("category", activeSlug);
    api.get(`/api/community/posts?${params}`).then(r => {
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page, sort, activeSlug, lang]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    if (postIdParam) loadPost(parseInt(postIdParam));
  }, [postIdParam]); // eslint-disable-line

  const loadPost = (id) => {
    api.get(`/api/community/posts/${id}?lang=${lang}`).then(r => setViewPost(r.data)).catch(() => showToast("Not found"));
  };

  const changeCategory = (s) => {
    setActiveSlug(s);
    setPage(1);
    setViewPost(null);
    navigate(s ? `/community/${s}` : "/community", { replace: true });
  };

  const ensureNickname = () => {
    if (!user) { navigate("/login", { state: { from: "/community" } }); return false; }
    if (!user.nickname) { setNicknameModal(true); return false; }
    return true;
  };

  const openWrite = (asReview = false) => {
    if (!ensureNickname()) return;
    setViewPost(null);
    setWriteAsReview(asReview);
    setShowWrite(true);
  };

  const saveNickname = () => {
    if (!nickInput.trim()) return;
    api.put("/api/auth/nickname", { nickname: nickInput.trim() }).then(() => {
      user.nickname = nickInput.trim();
      setNicknameModal(false);
      showToast(t.setNickname + " OK");
    }).catch(err => setNickError(err.response?.data?.error || "Error"));
  };

  const vote = (type, id) => {
    if (!ensureNickname()) return;
    api.post("/api/community/vote", { target_type: type, target_id: id }).then(() => {
      if (viewPost) loadPost(viewPost.id);
      else fetchPosts();
    }).catch(err => showToast(err.response?.data?.error || "Error"));
  };

  const showToast = (msg) => setToast(msg);
  useEffect(() => { if (toast) { const ti = setTimeout(() => setToast(""), 3000); return () => clearTimeout(ti); } }, [toast]);

  return (
    <div className="dept-community-page min-h-screen">
      {/* Toast */}
      {toast && <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl shadow-lg animate-fadeIn">{toast}</div>}

      {/* Hero banner */}
      <EditorialHero
        eyebrow="Community · 커뮤니티"
        titleTop={t.community}
        titleItalic="후기와 정보"
        description={t.communityDesc}
        minHeight={340}
      />

    <div className="max-w-4xl mx-auto px-4 py-8">

      {/* Header actions */}
      <div className="flex items-center justify-end mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => openWrite(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-100 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition">
            <SvgStarFill className="w-3.5 h-3.5" /> 리뷰 작성
          </button>
          <button onClick={() => setShowInquiry(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-[#6e6e73] hover:bg-gray-100 transition">
            <SvgGlobe className="w-3.5 h-3.5" /> {t.globalInquiry}
          </button>
        </div>
      </div>

      {/* Nickname modal */}
      {nicknameModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setNicknameModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[380px] shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#1d1d1f] mb-2">{t.setNickname}</h3>
            <p className="text-sm text-[#86868b] mb-4">{t.nickDesc}</p>
            <input value={nickInput} onChange={e => { setNickInput(e.target.value); setNickError(""); }}
              placeholder={t.nickPlaceholder}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none" />
            {nickError && <p className="text-xs text-red-500 mt-1">{nickError}</p>}
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setNicknameModal(false)} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm">{t.cancel}</button>
              <button onClick={saveNickname} className="px-4 py-2 rounded-lg bg-[#0071e3] text-white text-sm font-medium">{t.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* Global inquiry modal */}
      {showInquiry && (
        <GlobalInquiryModal lang={lang} t={t} onClose={() => setShowInquiry(false)} onSuccess={(msg) => { setShowInquiry(false); showToast(msg); }} />
      )}

      {/* Post detail view */}
      {viewPost ? (
        <PostDetail post={viewPost} user={user} vote={vote} lang={lang} t={t}
          onBack={() => { setViewPost(null); navigate(activeSlug ? `/community/${activeSlug}` : "/community", { replace: true }); }}
          ensureNickname={ensureNickname} showToast={showToast} reload={() => loadPost(viewPost.id)} />
      ) : showWrite ? (
        <WriteForm categories={categories} activeSlug={activeSlug} user={user} lang={lang} t={t} initialIsReview={writeAsReview}
          onCancel={() => { setShowWrite(false); setWriteAsReview(false); }} onDone={(newPost) => { setShowWrite(false); setWriteAsReview(false); showToast(t.submit); fetchPosts(); if (newPost?.id) loadPost(newPost.id); }} />
      ) : (
        <>
          {/* Categories */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
            <button onClick={() => changeCategory("")}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition ${!activeSlug ? "bg-[#1d1d1f] text-white" : "bg-gray-100 text-[#6e6e73] hover:bg-gray-200"}`}>
              {t.all}
            </button>
            {categories.map(c => (
              <button key={c.slug} onClick={() => changeCategory(c.slug)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${activeSlug === c.slug ? "bg-[#1d1d1f] text-white" : "bg-gray-100 text-[#6e6e73] hover:bg-gray-200"}`}>
                {c.svg_icon && <span className="w-4 h-4 inline-flex items-center [&>svg]:w-4 [&>svg]:h-4" dangerouslySetInnerHTML={{ __html: c.svg_icon }} />}
                {c.name || c.name_ko}
                {c.post_count > 0 && <span className="text-[10px] opacity-60">{c.post_count}</span>}
              </button>
            ))}
          </div>

          {/* Review Summary Box */}
          <ReviewSummary category={activeSlug} lang={lang} />

          {/* Sort + Write */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              {[
                { k: "latest", l: t.latest, icon: null },
                { k: "hot", l: t.hot, icon: <SvgFire className="w-3 h-3" /> },
                { k: "top", l: t.top, icon: <SvgTrophy className="w-3 h-3" /> }
              ].map(s => (
                <button key={s.k} onClick={() => { setSort(s.k); setPage(1); }}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${sort === s.k ? "bg-white text-[#1d1d1f] shadow-sm" : "text-[#86868b]"}`}>
                  {s.icon}{s.l}
                </button>
              ))}
            </div>
            <button onClick={() => openWrite(false)}
              className="px-4 py-2 rounded-xl bg-[#0071e3] text-white text-sm font-medium hover:bg-[#0077ed] transition flex items-center gap-1.5">
              <SvgPlus /> {t.write}
            </button>
          </div>

          {/* Posts list */}
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#86868b] text-lg mb-2">{t.noPostsYet}</p>
              <p className="text-sm text-gray-400">{t.beFirst}</p>
              <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                <button onClick={() => openWrite(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition">
                  <SvgStarFill className="w-4 h-4" /> 첫 리뷰 작성
                </button>
                <button onClick={() => openWrite(false)}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#0071e3] text-white text-sm font-semibold hover:bg-[#0077ed] transition">
                  <SvgPlus /> 글쓰기
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {posts.map(p => (
                <article key={p.id} onClick={() => { loadPost(p.id); navigate(`/community${activeSlug ? "/" + activeSlug : ""}?post=${p.id}`, { replace: true }); }}
                  className={`bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition p-4 cursor-pointer ${p.is_pinned ? "border-l-4 border-l-blue-400 bg-blue-50/20" : ""}`}>
                  <div className="flex gap-3">
                    {/* Upvote column */}
                    <div className="flex flex-col items-center gap-0.5 shrink-0 select-none" onClick={e => e.stopPropagation()}>
                      <button onClick={() => vote("post", p.id)}
                        className={`p-1 rounded hover:bg-orange-50 transition ${p.my_vote === 1 ? "text-orange-500" : "text-gray-300 hover:text-orange-400"}`}>
                        <SvgArrowUp />
                      </button>
                      <span className={`text-sm font-bold min-w-[20px] text-center ${p.upvotes > 0 ? "text-orange-500" : "text-gray-400"}`}>
                        {p.upvotes || 0}
                      </span>
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {p.is_pinned && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">
                            <SvgPin /> {t.pinned}
                          </span>
                        )}
                        {p.is_review && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">
                            <SvgStarFill className="w-2.5 h-2.5" /> {t.review}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-[#6e6e73]">
                          {p.category_icon && <span className="w-3 h-3 inline-flex [&>svg]:w-3 [&>svg]:h-3" dangerouslySetInnerHTML={{ __html: p.category_icon }} />}
                          {p.category_name_i18n || p.category_name}
                        </span>
                        {p.lang && p.lang !== "ko" && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-gray-50 text-[#86868b] uppercase">{p.lang}</span>
                        )}
                      </div>
                      <h3 className="text-[15px] font-semibold text-[#1d1d1f] truncate">{p.title}</h3>
                      {p.is_review && p.rating && (
                        <div className="mt-0.5"><StarRating rating={p.rating} /></div>
                      )}
                      <p className="text-sm text-[#6e6e73] line-clamp-2 mt-0.5">{p.preview?.replace(/[#*`<>[\]]/g, "").slice(0, 120)}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[#86868b]">
                        <span className="font-medium text-[#6e6e73]">{p.author}</span>
                        {p.author_badge && <BadgeTag level={p.author_badge} name={p.author_badge_name} />}
                        <span>{timeSince(p.created_at, lang)}</span>
                        <span className="flex items-center gap-0.5"><SvgComment className="w-3 h-3" /> {p.comment_count}</span>
                        <span className="flex items-center gap-0.5"><SvgEye className="w-3 h-3" /> {p.view_count}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > 20 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: Math.min(Math.ceil(total / 20), 10) }, (_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${page === i + 1 ? "bg-[#1d1d1f] text-white" : "bg-gray-100 text-[#6e6e73] hover:bg-gray-200"}`}>{i + 1}</button>
              ))}
            </div>
          )}

          {/* Business CTA */}
          <div className="mt-8 grid grid-cols-2 gap-3">
            <a href="/consultation?tab=general&type=as#consultation-form" className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-white border border-blue-100 hover:shadow-sm transition">
              <SvgWrench className="w-5 h-5 text-[#0071e3]" />
              <div>
                <p className="text-sm font-semibold text-[#1d1d1f]">{t.serviceCta}</p>
                <p className="text-[11px] text-[#86868b]">{t.serviceCtaDesc}</p>
              </div>
            </a>
            <a href="/consultation?tab=rental#consultation-form" className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-green-50 to-white border border-green-100 hover:shadow-sm transition">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
              <div>
                <p className="text-sm font-semibold text-[#1d1d1f]">{t.rentalCta}</p>
                <p className="text-[11px] text-[#86868b]">{t.rentalCtaDesc}</p>
              </div>
            </a>
          </div>
        </>
      )}
    </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   POST DETAIL
   ═══════════════════════════════════════════════════════════ */
function PostDetail({ post, user, vote, onBack, ensureNickname, showToast, reload, lang, t }) {
  const [comment, setComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editContent, setEditContent] = useState(post.content);
  const [editFormat, setEditFormat] = useState(post.content_format || "text");

  const isAuthor = user && user.id === post.author_id;
  const isAdmin = isAdminRole(user?.role);

  const submitComment = () => {
    if (!ensureNickname() || !comment.trim()) return;
    const body = { content: comment.trim() };
    if (replyTo) body.parent_id = replyTo;
    api.post(`/api/community/posts/${post.id}/comments`, body).then(() => {
      setComment(""); setReplyTo(null); showToast(t.submit); reload();
    }).catch(err => showToast(err.response?.data?.error || "Error"));
  };

  const deleteComment = (cid) => {
    if (!window.confirm(t.deleteConfirm || "Delete?")) return;
    api.delete(`/api/community/comments/${cid}`).then(() => { showToast(t.deleted || "Deleted"); reload(); });
  };

  const saveEdit = () => {
    api.put(`/api/community/posts/${post.id}`, { title: editTitle, content: editContent, content_format: editFormat }).then(() => {
      setEditing(false); showToast(t.submit); reload();
    }).catch(err => showToast(err.response?.data?.error || "Error"));
  };

  const deletePost = () => {
    if (!window.confirm(t.deleteConfirm || "Delete?")) return;
    api.delete(`/api/community/posts/${post.id}`).then(() => { showToast(t.deleted || "Deleted"); onBack(); });
  };

  const topComments = (post.comments || []).filter(c => !c.parent_id);
  const replies = (post.comments || []).filter(c => c.parent_id);
  const getReplies = (parentId) => replies.filter(r => r.parent_id === parentId);

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#0071e3] hover:underline mb-4">
        <SvgBack /> {t.backToList}
      </button>

      {post.is_blinded ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <p className="text-red-600 font-medium">{t.blinded}</p>
          <p className="text-sm text-red-400 mt-1">{post.blind_reason}</p>
        </div>
      ) : editing ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full px-4 py-3 rounded-xl border text-[15px] font-semibold focus:ring-2 focus:ring-blue-100 outline-none" />
          <MarkdownEditor
            value={editContent}
            onChange={setEditContent}
            contentFormat={editFormat}
            onFormatChange={setEditFormat}
            uploadEndpoint="/api/community/upload-image"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm">{t.cancel}</button>
            <button onClick={saveEdit} className="px-4 py-2 rounded-lg bg-[#0071e3] text-white text-sm font-medium">{t.confirm}</button>
          </div>
        </div>
      ) : (
        <article className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-[#6e6e73] inline-flex items-center gap-1">
              {post.category_icon && <span className="w-3.5 h-3.5 inline-flex [&>svg]:w-3.5 [&>svg]:h-3.5" dangerouslySetInnerHTML={{ __html: post.category_icon }} />}
              {post.category_name_i18n || post.category_name}
            </span>
            {post.is_pinned && <span className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-600"><SvgPin /> {t.pinned}</span>}
            {post.is_review && <span className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600"><SvgStarFill className="w-3 h-3" /> {t.review}</span>}
            {post.lang && post.lang !== "ko" && <span className="text-[10px] px-1 py-0.5 rounded bg-gray-50 text-[#86868b] uppercase">{post.lang}</span>}
          </div>
          <h2 className="text-xl font-bold text-[#1d1d1f] mb-2">{post.title}</h2>
          {post.is_review && post.rating && (
            <div className="mb-3"><StarRating rating={post.rating} size="md" /></div>
          )}
          <div className="flex items-center gap-3 text-[12px] text-[#86868b] mb-4">
            <span className="font-medium text-[#6e6e73]">{post.author || post.author_nickname}</span>
            {post.author_badge && <BadgeTag level={post.author_badge} name={post.author_badge_name} />}
            <span>{timeSince(post.created_at, lang)}</span>
            <span className="flex items-center gap-0.5"><SvgEye className="w-3.5 h-3.5" /> {post.view_count}</span>
          </div>
          <div className="text-sm text-[#1d1d1f] leading-relaxed whitespace-pre-wrap mb-4">
            <RichContent content={post.content} format={post.content_format || "text"} />
          </div>

          {/* Vote bar — upvote only */}
          <div className="flex items-center gap-3 border-t border-gray-100 pt-4">
            <button onClick={() => vote("post", post.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${post.my_vote === 1 ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-[#6e6e73] hover:bg-orange-50"}`}>
              <SvgArrowUp /> {t.upvote} {post.upvotes}
            </button>
            <span className="ml-auto flex gap-2">
              {(isAuthor || isAdmin) && <button onClick={() => setEditing(true)} className="text-xs text-[#0071e3] hover:underline">{t.edit}</button>}
              {(isAuthor || isAdmin) && <button onClick={deletePost} className="text-xs text-red-500 hover:underline">{t.delete}</button>}
            </span>
          </div>
        </article>
      )}

      {/* Comments section */}
      <div className="mt-6">
        <h3 className="text-sm font-bold text-[#1d1d1f] mb-3 flex items-center gap-1.5">
          <SvgComment className="w-4 h-4" /> {t.comments} {post.comment_count}{lang === "ko" ? "개" : ""}
        </h3>

        {/* Comment input */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4">
          {replyTo && (
            <div className="flex items-center gap-2 mb-2 text-xs text-[#0071e3]">
              <SvgReply className="w-3 h-3" /> <span>{t.reply}</span>
              <button onClick={() => setReplyTo(null)} className="text-red-400 hover:text-red-500">{t.cancel}</button>
            </div>
          )}
          <div className="flex gap-2">
            <input value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitComment()}
              placeholder={user ? t.commentPlaceholder : t.loginRequired}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-100 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none" />
            <button onClick={submitComment} className="px-4 py-2.5 rounded-lg bg-[#0071e3] text-white text-sm font-medium hover:bg-[#0077ed] transition shrink-0">{t.submit}</button>
          </div>
        </div>

        {/* Comments list */}
        <div className="space-y-2">
          {topComments.map(c => (
            <CommentItem key={c.id} comment={c} replies={getReplies(c.id)} user={user} vote={vote} lang={lang} t={t}
              onReply={(id) => setReplyTo(id)} onDelete={deleteComment} isAdmin={isAdmin} />
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   COMMENT ITEM — upvote only
   ═══════════════════════════════════════════════════════════ */
function CommentItem({ comment: c, replies, user, vote, onReply, onDelete, isAdmin, lang, t, depth = 0 }) {
  const isAuthor = user && user.id === c.author_id;
  return (
    <div className={depth > 0 ? "ml-8 border-l-2 border-gray-100 pl-4" : ""}>
      <div className="bg-white rounded-lg border border-gray-50 p-3 hover:bg-gray-50/50 transition">
        {c.is_blinded ? (
          <p className="text-sm text-gray-400 italic">{t.blinded}</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-[#1d1d1f]">{c.author_nickname || c.author}</span>
              {c.author_badge && <BadgeTag level={c.author_badge} name={c.author_badge_name} />}
              <span className="text-[10px] text-[#86868b]">{timeSince(c.created_at, lang)}</span>
            </div>
            <p className="text-sm text-[#1d1d1f] whitespace-pre-wrap">{c.content}</p>
            <div className="flex items-center gap-3 mt-2 text-[11px]">
              <button onClick={() => vote("comment", c.id)}
                className={`flex items-center gap-0.5 ${c.my_vote === 1 ? "text-orange-500 font-medium" : "text-gray-400 hover:text-orange-400"}`}>
                <SvgArrowUp className="w-3.5 h-3.5" /> {c.upvotes}
              </button>
              {depth === 0 && (
                <button onClick={() => onReply(c.id)} className="text-[#0071e3] hover:underline flex items-center gap-0.5">
                  <SvgReply className="w-3 h-3" /> {t.reply}
                </button>
              )}
              {(isAuthor || isAdmin) && <button onClick={() => onDelete(c.id)} className="text-red-400 hover:text-red-500">{t.delete}</button>}
            </div>
          </>
        )}
      </div>
      {replies && replies.map(r => (
        <CommentItem key={r.id} comment={r} replies={[]} user={user} vote={vote} onReply={onReply} onDelete={onDelete} isAdmin={isAdmin} lang={lang} t={t} depth={depth + 1} />
      ))}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   WRITE FORM — i18n aware
   ═══════════════════════════════════════════════════════════ */
function WriteForm({ categories, activeSlug, user, lang, t, initialIsReview = false, onCancel, onDone }) {
  const [catId, setCatId] = useState(categories.find(c => c.slug === activeSlug)?.id || (categories[0]?.id || ""));
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contentFormat, setContentFormat] = useState("markdown");
  const [postLang, setPostLang] = useState(lang);
  const [isReview, setIsReview] = useState(initialIsReview);
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setIsReview(initialIsReview);
  }, [initialIsReview]);

  const submit = () => {
    if (!title.trim() || !content.trim() || !catId) { setError(t.selectAll); return; }
    setSubmitting(true);
    const body = {
      category_id: catId,
      title: title.trim(),
      content: content.trim(),
      content_format: contentFormat,
      lang: postLang,
      is_review: isReview,
    };
    if (isReview) body.rating = rating;
    api.post("/api/community/posts", body)
      .then(r => onDone(r.data))
      .catch(err => { setError(err.response?.data?.error || "Error"); setSubmitting(false); });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold text-[#1d1d1f]">{t.writeNew}</h2>
        <button onClick={onCancel} className="text-sm text-[#86868b] hover:text-[#1d1d1f]">{t.cancel}</button>
      </div>

      <div className="flex gap-2">
        <select value={catId} onChange={e => setCatId(Number(e.target.value))}
          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-100 outline-none">
          {categories.map(c => <option key={c.id} value={c.id}>{c.name || c.name_ko} — {c.description || c.desc_ko}</option>)}
        </select>
        <select value={postLang} onChange={e => setPostLang(e.target.value)}
          className="w-24 px-3 py-3 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-100 outline-none">
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>

      <input value={title} onChange={e => setTitle(e.target.value)} placeholder={t.title}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-[15px] font-medium focus:ring-2 focus:ring-blue-100 outline-none" />

      {/* Review toggle */}
      <div className="flex items-center gap-4 px-1">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={isReview} onChange={e => setIsReview(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
          <span className="text-sm text-[#1d1d1f] font-medium flex items-center gap-1">
            <SvgStarFill className="w-4 h-4 text-amber-400" /> {t.writeAsReview}
          </span>
        </label>
        {isReview && (
          <div className="flex items-center gap-2">
            <StarRatingInput rating={rating} setRating={setRating} />
            <span className="text-xs text-[#86868b]">{rating}/5</span>
          </div>
        )}
      </div>

      {/* Rich text editor */}
      <MarkdownEditor
        value={content}
        onChange={setContent}
        contentFormat={contentFormat}
        onFormatChange={setContentFormat}
        uploadEndpoint="/api/community/upload-image"
      />

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting}
          className="px-6 py-3 rounded-xl bg-[#0071e3] text-white text-sm font-medium hover:bg-[#0077ed] transition disabled:opacity-50">
          {submitting ? t.submitting : t.submit}
        </button>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   GLOBAL INQUIRY MODAL — overseas users
   ═══════════════════════════════════════════════════════════ */
function GlobalInquiryModal({ lang, t, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", country: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.name || !form.email || !form.subject || !form.message) { setErr(t.selectAll); return; }
    setSending(true);
    api.post("/api/global-inquiry", { ...form, lang })
      .then(() => onSuccess(t.inquirySuccess))
      .catch(e => { setErr(e.response?.data?.error || "Error"); setSending(false); });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-[460px] shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1d1d1f] flex items-center gap-2"><SvgGlobe className="w-5 h-5 text-[#0071e3]" /> {t.globalInquiry}</h3>
          <button onClick={onClose} className="text-[#86868b] hover:text-[#1d1d1f] text-xl">&times;</button>
        </div>
        <p className="text-sm text-[#86868b] mb-4">{t.globalInquiryDesc}</p>
        <div className="space-y-3">
          {[
            { k: "name", p: t.name },
            { k: "email", p: t.email, type: "email" },
            { k: "country", p: t.country },
            { k: "subject", p: t.subject }
          ].map(f => (
            <input key={f.k} value={form[f.k]} onChange={e => handle(f.k, e.target.value)}
              type={f.type || "text"} placeholder={f.p}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-100 outline-none" />
          ))}
          <textarea value={form.message} onChange={e => handle("message", e.target.value)}
            placeholder={t.message} rows={4}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm resize-y focus:ring-2 focus:ring-blue-100 outline-none" />
        </div>
        {err && <p className="text-xs text-red-500 mt-2">{err}</p>}
        <div className="flex gap-2 mt-4 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm">{t.cancel}</button>
          <button onClick={submit} disabled={sending}
            className="px-4 py-2 rounded-lg bg-[#0071e3] text-white text-sm font-medium disabled:opacity-50">{sending ? "..." : t.submit}</button>
        </div>
      </div>
    </div>
  );
}
